import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { type preferredTimeDisplayModeTypes } from 'types/customer.types';

import type {
  DriverBehavior,
  DriverBehaviorData,
  DriverBehaviorTimeLineData,
  EngineHourDriverBehaviourEventConfig,
} from 'types/driver.behavior.types';
import { convertUtcToDisplayTime } from './helpers/dateTime';

dayjs.extend(utc);

// Maps the stable `key` field of each driver behavior event (used to match
// against API event-type data) to its i18n translation key for display.
// The `name` field must never be translated/renamed here since it also
// doubles as a lookup key elsewhere (e.g. EventDetailModal).
export const driverBehaviorEventTitleKeys: Record<string, string> = {
  overspeed: 'driver_behavior_event_overspeeding',
  underspeed: 'driver_behavior_event_underspeeding',
  incorrect_gear: 'driver_behavior_event_incorrect_gear_to_speed_ratio',
  braking: 'driver_behavior_event_hard_braking',
  acceleration: 'driver_behavior_event_hard_acceleration',
  gear_skip: 'driver_behavior_event_gear_skip',
};

export const allEvents: EngineHourDriverBehaviourEventConfig[] = [
  {
    key: 'overspeed',
    name: 'overspeeding',
    tooltipConfig: { showDuration: true, showAvgSpeed: true },
  },
  {
    key: 'underspeed',
    name: 'underspeeding',
    tooltipConfig: { showDuration: true, showAvgSpeed: true },
  },
  {
    key: 'incorrect_gear',
    name: 'incorrect gear to speed ratio',
    tooltipConfig: {
      showGear: true,
      showDuration: true,
    },
  },
  {
    key: 'braking',
    name: 'hard braking',
    isInstantEvent: true,
    tooltipConfig: {
      showCurrentAndPreviousSpeed: true,
    },
  },
  {
    key: 'acceleration',
    name: 'hard acceleration',
    isInstantEvent: true,
    tooltipConfig: {
      showCurrentAndPreviousSpeed: true,
    },
  },
  {
    key: 'gear_skip',
    name: 'Gear Skip',
    isInstantEvent: true,
    tooltipConfig: {
      showCurrentAndNextGear: true,
      showSpeed: true,
    },
  },
];

export const allEngineHoursEvents: EngineHourDriverBehaviourEventConfig[] = [
  {
    key: 'short_idling',
    name: 'short_idling',
    tooltipConfig: { showDuration: true },
  },
  {
    key: 'excess_idling',
    name: 'excess_idling',
    tooltipConfig: { showDuration: true },
  },
  {
    key: 'loading_spotting',
    name: 'loading_spotting',
    tooltipConfig: { showDuration: true },
  },
  {
    key: 'unloading_spotting',
    name: 'unloading_spotting',
    tooltipConfig: { showDuration: true },
  },
  {
    key: 'loading_stop',
    name: 'loading_stop',
    tooltipConfig: { showDuration: true },
  },
  {
    key: 'dumping_stop',
    name: 'dumping_stop',
    tooltipConfig: { showDuration: true },
  },
  {
    key: 'queuing',
    name: 'queuing',
    tooltipConfig: { showDuration: true },
  },
  {
    key: 'queuing_moving',
    name: 'queuing_moving',
    tooltipConfig: { showDuration: true },
  },
  {
    key: 'queuing_stopped',
    name: 'queuing_stopped',
    tooltipConfig: { showDuration: true },
  },
  {
    key: 'pre_loading_queuing_stopped',
    name: 'pre_loading_queuing_stopped',
    tooltipConfig: { showDuration: true },
  },
  {
    key: 'pre_dumping_queuing_moving',
    name: 'pre_dumping_queuing_moving',
    tooltipConfig: { showDuration: true },
  },
  {
    key: 'pre_dumping_queuing_stopped',
    name: 'pre_dumping_queuing_stopped',
    tooltipConfig: { showDuration: true },
  },
  {
    key: 'main_parking',
    name: 'main_parking',
    tooltipConfig: { showDuration: true },
  },
  {
    key: 'lunch_parking',
    name: 'lunch_parking',
    tooltipConfig: { showDuration: true },
  },
  {
    key: 'main_parking',
    name: 'main_parking',
    tooltipConfig: { showDuration: true },
  },
  {
    key: 'lunch_parking',
    name: 'lunch_parking',
    tooltipConfig: { showDuration: true },
  },
  {
    key: 'mucking',
    name: 'mucking',
    tooltipConfig: { showDuration: true },
  },
  {
    key: 'moving_loaded',
    name: 'moving_loaded',
    tooltipConfig: { showDuration: true },
  },
  {
    key: 'moving_unloaded',
    name: 'moving_unloaded',
    tooltipConfig: { showDuration: true },
  },
  {
    key: 'productive_moving',
    name: 'productive_moving',
    tooltipConfig: { showDuration: true },
  },
  {
    key: 'allowed_break',
    name: 'allowed_break',
    tooltipConfig: { showDuration: true },
  },
  {
    key: 'shutdown',
    name: 'shutdown',
    tooltipConfig: { showDuration: true },
  },
  {
    key: 'unknown',
    name: 'unknown',
    tooltipConfig: { showDuration: true },
  },
  {
    key: 'maintenance',
    name: 'maintenance',
    tooltipConfig: { showDuration: true },
  },
  {
    key: 'fuel_bay',
    name: 'fuel_bay',
    tooltipConfig: { showDuration: true },
  },
];

export const loaderNewEvents = [
  {
    key: 'moving_loaded',
    name: 'moving_loaded',
  },
  {
    key: 'moving_unloaded',
    name: 'moving_unloaded',
  },
  {
    key: 'mucking',
    name: 'mucking',
  },
  {
    key: 'excess_idling',
    name: 'excess_idling',
  },
  {
    key: 'queuing',
    name: 'queuing',
  },
  {
    key: 'allowed_break',
    name: 'allowed_break',
  },
];

export function normalizeEventPercentage(value?: number): number {
  if (typeof value !== 'number' || Number.isNaN(value)) return 0;
  if (Math.abs(value) > 1000) {
    return value / 100;
  }
  return value;
}

export function aggregateEvents(
  data: DriverBehavior[],
  preferredTimeDisplayMode: preferredTimeDisplayModeTypes,
  timezone: string
) {
  const eventMap: Record<string, number> = {};

  data.forEach((item) => {
    item.data?.forEach((event) => {
      // timezone based data
      const startDate = convertUtcToDisplayTime(
        event?.start_ts,
        preferredTimeDisplayMode,
        timezone,
        'YYYY-MM-DD'
      );

      // event?.start_ts?.split(' ')[0]; // Extract date portion
      if (!eventMap[startDate]) {
        eventMap[startDate] = 0;
      }
      eventMap[startDate] += 1; // Increment count for this date
    });
  });

  // Convert the map to an array of objects
  const result = Object.entries(eventMap).map(([date, count]) => ({
    start_ts: date,
    noOfEvents: count,
  }));
  result.sort((a, b) => dayjs(a.start_ts).unix() - dayjs(b.start_ts).unix());
  return result;
}

export function transformDriverBehaviorData(
  driverBehaviorData: DriverBehaviorData,
  engineHours?: boolean
): DriverBehaviorTimeLineData[] {
  const mapEventsToTimelineData = (
    behaviors: DriverBehavior[],
    name: string
  ): DriverBehaviorTimeLineData => {
    const list = behaviors ?? [];
    return {
      name,
      data: list.flatMap((behavior) => {
        const assetKey =
          behavior.bumper_id ??
          (behavior.device_id &&
          behavior.device_id !== 'FLEET-WIDE' &&
          behavior.device_id !== 'fleet-wide'
            ? behavior.device_id
            : undefined);
        if (!assetKey) {
          return [];
        }
        return (
          behavior.data?.map((event) => {
            const startTs = event?.start_ts;
            const endTs = event?.end_ts;

            return {
              x: String(assetKey),
              y: [dayjs.utc(startTs).valueOf(), dayjs.utc(endTs).valueOf()],
              asset_type: behavior?.asset_type,
              ...event,
            };
          }) ?? []
        );
      }),
    };
  };

  const eventsToUse = engineHours ? allEngineHoursEvents : allEvents;

  const resp: DriverBehaviorTimeLineData[] = eventsToUse?.map((item) => {
    const eventData = mapEventsToTimelineData(
      driverBehaviorData?.[item?.key],
      item?.name
    );

    return eventData;
  });

  return resp;
}

export function filterDriverBehaviorByDeviceId(
  data: DriverBehaviorData,
  bumperId: string
): DriverBehaviorData {
  const filterByDeviceId = (behaviorList: DriverBehavior[]) =>
    behaviorList.filter((behavior) => behavior.bumper_id === bumperId);

  return {
    productive: filterByDeviceId(data.productive),
    short_idling: filterByDeviceId(data.short_idling),
    excess_idling: filterByDeviceId(data.excess_idling),
    overspeed: filterByDeviceId(data.overspeed),
    underspeed: filterByDeviceId(data.underspeed),
    braking: filterByDeviceId(data.braking),
    incorrect_gear: filterByDeviceId(data?.incorrect_gear),
    gear_skip: filterByDeviceId(data?.gear_skip),
    engine_hours: [],
    acceleration: filterByDeviceId(data.acceleration),
  };
}

export function isAllTimeLineDataEmpty(arr: any) {
  return arr.every(
    (item: any) => Array.isArray(item.data) && item.data.length === 0
  );
}

export function transformInclinometerEvents(
  driverBehaviorData: DriverBehaviorData,
  engineHours?: boolean
): DriverBehaviorTimeLineData[] {
  const mapEventsToTimelineData = (
    behaviors: DriverBehavior[] | DriverBehavior | undefined,
    name: string
  ): DriverBehaviorTimeLineData => {
    const normalized = Array.isArray(behaviors)
      ? behaviors
      : behaviors
      ? [behaviors]
      : [];

    return {
      name,
      data: normalized.flatMap(
        (behavior) =>
          behavior.data?.map((event) => ({
            x: behavior.bumper_id,
            y: [
              new Date(event?.start_ts).getTime(),
              new Date(event?.end_ts).getTime(),
            ],
            ...event,
          })) ?? []
      ),
    };
  };

  const allEvents = [
    { key: 'braking', name: 'Braking' },
    { key: 'productive', name: 'Productive' },
    { key: 'short_idling', name: 'Short Idling' },
    { key: 'excess_idling', name: 'Excess Idling' },
    { key: 'overspeed', name: 'Overspeed' },
    { key: 'underspeed', name: 'Underspeed' },
    { key: 'incorrect_gear', name: 'Incorrect Gear' },
    { key: 'engine_hours', name: 'Engine Hours' },
    { key: 'acceleration', name: 'Acceleration' },
    { key: 'gear_skip', name: 'Gear Skip' },
  ];

  const eventsToUse = engineHours
    ? allEvents.filter((e) => e.key === 'engine_hours')
    : allEvents;

  return eventsToUse.map((item) =>
    mapEventsToTimelineData(driverBehaviorData?.[item.key], item.name)
  );
}
